/**
 * Базовая реализация State Machine для CSElement
 */

import { EventEmitter } from 'eventemitter3';
import {
  IStateMachine,
  StateMachineConfig,
  StateMachineContext,
  StateMachineEvent,
  StateMachineEventType,
  StateMachineEventCallback,
  State,
  Transition,
  TransitionResult,
  TransitionHistoryEntry
} from '../types/state-machine-interfaces';
import { ICSElement } from '../types/interfaces';

/**
 * Реализация State Machine
 */
export class StateMachine extends EventEmitter implements IStateMachine {
  private _config: StateMachineConfig;
  private _context: StateMachineContext;
  private _states: Map<string, State>;
  private _transitions: Map<string, Transition>;
  private _transitionsByEvent: Map<string, Transition[]>;
  private _callbacks: Map<StateMachineEventType, Map<string, StateMachineEventCallback>>;
  private _destroyed: boolean = false;

  constructor(element: ICSElement, config: StateMachineConfig) {
    super();
    
    this._config = { ...config };
    this._states = new Map();
    this._transitions = new Map();
    this._transitionsByEvent = new Map();
    this._callbacks = new Map();

    // Инициализируем карты событий
    Object.values(StateMachineEventType).forEach(eventType => {
      this._callbacks.set(eventType, new Map());
    });

    // Валидируем конфигурацию
    this.validateConfig();

    // Инициализируем состояния и переходы
    this.initializeStates();
    this.initializeTransitions();

    // Создаем контекст
    this._context = {
      element,
      currentState: this._config.initialState,
      data: new Map(),
      history: [],
      metadata: {}
    };

    // Входим в начальное состояние
    this.enterState(this._config.initialState);

    if (this._config.enableLogging) {
      console.log(`🔄 State Machine "${this.name}" создан в состоянии "${this.currentState}"`);
    }
  }

  // ==================== Геттеры ====================

  get name(): string {
    return this._config.name;
  }

  get currentState(): string {
    return this._context.currentState;
  }

  get context(): StateMachineContext {
    return { ...this._context };
  }

  get config(): StateMachineConfig {
    return { ...this._config };
  }

  // ==================== Основные методы ====================

  /**
   * Отправить событие в state machine
   */
  async send(event: string | StateMachineEvent, payload?: any): Promise<TransitionResult> {
    if (this._destroyed) {
      throw new Error('State Machine уничтожен');
    }

    const smEvent: StateMachineEvent = typeof event === 'string' 
      ? { type: event, payload, timestamp: Date.now() }
      : event;

    const startTime = Date.now();
    
    if (this._config.enableLogging) {
      console.log(`🔄 State Machine "${this.name}": получено событие "${smEvent.type}" в состоянии "${this.currentState}"`);
    }

    // Находим возможные переходы для этого события
    const possibleTransitions = this.findTransitionsForEvent(smEvent.type);
    
    if (possibleTransitions.length === 0) {
      const result: TransitionResult = {
        success: false,
        fromState: this.currentState,
        toState: this.currentState,
        event: smEvent,
        executedActions: [],
        error: `Нет переходов для события "${smEvent.type}" из состояния "${this.currentState}"`,
        duration: Date.now() - startTime
      };

      this.emitEvent(StateMachineEventType.TRANSITION_FAILED, result);
      return result;
    }

    // Сортируем переходы по приоритету
    possibleTransitions.sort((a, b) => (b.priority || 0) - (a.priority || 0));

    // Пытаемся выполнить первый подходящий переход
    for (const transition of possibleTransitions) {
      if (await this.canExecuteTransition(transition, smEvent)) {
        return await this.executeTransition(transition, smEvent, startTime);
      }
    }

    // Если ни один переход не подошел
    const result: TransitionResult = {
      success: false,
      fromState: this.currentState,
      toState: this.currentState,
      event: smEvent,
      executedActions: [],
      error: `Все переходы для события "${smEvent.type}" заблокированы условиями`,
      duration: Date.now() - startTime
    };

    this.emitEvent(StateMachineEventType.TRANSITION_FAILED, result);
    return result;
  }

  /**
   * Проверить, возможен ли переход
   */
  canTransition(event: string, toState?: string): boolean {
    const transitions = this.findTransitionsForEvent(event);
    
    if (toState) {
      return transitions.some(t => t.to === toState);
    }
    
    return transitions.length > 0;
  }

  /**
   * Получить возможные переходы из текущего состояния
   */
  getPossibleTransitions(): Transition[] {
    return Array.from(this._transitions.values())
      .filter(t => t.from === this.currentState);
  }

  /**
   * Получить состояние по имени
   */
  getState(name: string): State | undefined {
    return this._states.get(name);
  }

  /**
   * Получить переход по ID
   */
  getTransition(id: string): Transition | undefined {
    return this._transitions.get(id);
  }

  /**
   * Получить историю переходов
   */
  getHistory(limit?: number): TransitionHistoryEntry[] {
    const history = [...this._context.history];
    return limit ? history.slice(-limit) : history;
  }

  /**
   * Сбросить state machine в начальное состояние
   */
  async reset(): Promise<void> {
    if (this._destroyed) {
      throw new Error('State Machine уничтожен');
    }

    const oldState = this.currentState;
    
    // Выходим из текущего состояния
    await this.exitState(oldState);
    
    // Очищаем контекст
    this._context.currentState = this._config.initialState;
    this._context.previousState = undefined;
    this._context.data.clear();
    this._context.history = [];
    this._context.metadata = {};
    
    // Входим в начальное состояние
    await this.enterState(this._config.initialState);

    if (this._config.enableLogging) {
      console.log(`🔄 State Machine "${this.name}" сброшен в состояние "${this.currentState}"`);
    }
  }

  /**
   * Уничтожить state machine
   */
  destroy(): void {
    if (this._destroyed) return;

    this._destroyed = true;
    this.removeAllListeners();
    this._callbacks.clear();
    
    if (this._config.enableLogging) {
      console.log(`🔄 State Machine "${this.name}" уничтожен`);
    }
  }

  /**
   * Подписаться на события state machine
   */
  subscribe(event: StateMachineEventType, callback: StateMachineEventCallback): string {
    const id = this.generateId();
    const callbacks = this._callbacks.get(event);
    if (callbacks) {
      callbacks.set(id, callback);
    }
    return id;
  }

  /**
   * Отписаться от событий
   */
  unsubscribe(event: StateMachineEventType, callbackId: string): boolean {
    const callbacks = this._callbacks.get(event);
    if (callbacks) {
      return callbacks.delete(callbackId);
    }
    return false;
  }

  // ==================== Приватные методы ====================

  private validateConfig(): void {
    if (!this._config.name) {
      throw new Error('Имя State Machine обязательно');
    }

    if (!this._config.initialState) {
      throw new Error('Начальное состояние обязательно');
    }

    if (!this._config.states || this._config.states.length === 0) {
      throw new Error('Должно быть хотя бы одно состояние');
    }

    // Проверяем, что начальное состояние существует
    const initialStateExists = this._config.states.some(s => s.name === this._config.initialState);
    if (!initialStateExists) {
      throw new Error(`Начальное состояние "${this._config.initialState}" не найдено в списке состояний`);
    }

    // Проверяем уникальность имен состояний
    const stateNames = new Set();
    for (const state of this._config.states) {
      if (stateNames.has(state.name)) {
        throw new Error(`Дублирующееся имя состояния: "${state.name}"`);
      }
      stateNames.add(state.name);
    }

    // Проверяем переходы
    if (this._config.transitions) {
      const transitionIds = new Set();
      for (const transition of this._config.transitions) {
        if (transitionIds.has(transition.id)) {
          throw new Error(`Дублирующийся ID перехода: "${transition.id}"`);
        }
        transitionIds.add(transition.id);

        // Проверяем, что состояния существуют
        if (!stateNames.has(transition.from)) {
          throw new Error(`Состояние "${transition.from}" в переходе "${transition.id}" не найдено`);
        }
        if (!stateNames.has(transition.to)) {
          throw new Error(`Состояние "${transition.to}" в переходе "${transition.id}" не найдено`);
        }
      }
    }
  }

  private initializeStates(): void {
    for (const state of this._config.states) {
      this._states.set(state.name, state);
    }
  }

  private initializeTransitions(): void {
    if (!this._config.transitions) return;

    for (const transition of this._config.transitions) {
      this._transitions.set(transition.id, transition);

      // Индексируем переходы по событиям
      if (!this._transitionsByEvent.has(transition.event)) {
        this._transitionsByEvent.set(transition.event, []);
      }
      this._transitionsByEvent.get(transition.event)!.push(transition);
    }
  }

  private findTransitionsForEvent(event: string): Transition[] {
    const allTransitions = this._transitionsByEvent.get(event) || [];
    return allTransitions.filter(t => t.from === this.currentState);
  }

  private async canExecuteTransition(transition: Transition, event: StateMachineEvent): Promise<boolean> {
    if (transition.guard) {
      try {
        return await transition.guard(this._context, event);
      } catch (error) {
        if (this._config.enableLogging) {
          console.error(`🔄 Ошибка в guard перехода "${transition.id}":`, error);
        }
        return false;
      }
    }
    return true;
  }

  private async executeTransition(transition: Transition, event: StateMachineEvent, startTime: number): Promise<TransitionResult> {
    const fromState = this.currentState;
    const executedActions: string[] = [];

    this.emitEvent(StateMachineEventType.TRANSITION_STARTED, {
      transition,
      event,
      fromState,
      toState: transition.to
    });

    try {
      // Выходим из текущего состояния
      await this.exitState(fromState);

      // Выполняем действия перехода
      if (transition.actions) {
        for (const action of transition.actions) {
          await action(this._context, event);
          executedActions.push(action.name || 'anonymous');
        }
      }

      // Обновляем контекст
      this._context.previousState = fromState;
      this._context.currentState = transition.to;

      // Входим в новое состояние
      await this.enterState(transition.to);

      // Добавляем в историю
      const historyEntry: TransitionHistoryEntry = {
        transitionId: transition.id,
        fromState,
        toState: transition.to,
        event,
        timestamp: Date.now(),
        duration: Date.now() - startTime
      };

      this._context.history.push(historyEntry);

      // Ограничиваем размер истории
      const maxHistorySize = this._config.maxHistorySize || 100;
      if (this._context.history.length > maxHistorySize) {
        this._context.history = this._context.history.slice(-maxHistorySize);
      }

      const result: TransitionResult = {
        success: true,
        fromState,
        toState: transition.to,
        event,
        executedActions,
        duration: Date.now() - startTime
      };

      this.emitEvent(StateMachineEventType.TRANSITION_COMPLETED, result);
      this.emitEvent(StateMachineEventType.STATE_CHANGED, {
        fromState,
        toState: transition.to,
        event
      });

      if (this._config.enableLogging) {
        console.log(`🔄 State Machine "${this.name}": переход "${fromState}" → "${transition.to}" выполнен`);
      }

      return result;

    } catch (error) {
      const result: TransitionResult = {
        success: false,
        fromState,
        toState: transition.to,
        event,
        executedActions,
        error: error instanceof Error ? error.message : String(error),
        duration: Date.now() - startTime
      };

      this.emitEvent(StateMachineEventType.TRANSITION_FAILED, result);
      this.emitEvent(StateMachineEventType.ERROR, { error, transition, event });

      if (this._config.enableLogging) {
        console.error(`🔄 Ошибка в переходе "${transition.id}":`, error);
      }

      return result;
    }
  }

  private async enterState(stateName: string): Promise<void> {
    const state = this._states.get(stateName);
    if (!state) return;

    if (state.onEnter) {
      for (const handler of state.onEnter) {
        try {
          await handler(this._context);
        } catch (error) {
          if (this._config.enableLogging) {
            console.error(`🔄 Ошибка в onEnter обработчике состояния "${stateName}":`, error);
          }
        }
      }
    }
  }

  private async exitState(stateName: string): Promise<void> {
    const state = this._states.get(stateName);
    if (!state) return;

    if (state.onExit) {
      for (const handler of state.onExit) {
        try {
          await handler(this._context);
        } catch (error) {
          if (this._config.enableLogging) {
            console.error(`🔄 Ошибка в onExit обработчике состояния "${stateName}":`, error);
          }
        }
      }
    }
  }

  private emitEvent(eventType: StateMachineEventType, data: any): void {
    const callbacks = this._callbacks.get(eventType);
    if (callbacks) {
      for (const callback of callbacks.values()) {
        try {
          callback(data);
        } catch (error) {
          if (this._config.enableLogging) {
            console.error(`🔄 Ошибка в callback события "${eventType}":`, error);
          }
        }
      }
    }
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }
} 